import useSWR from "swr";
import {
  getAllLogsDetails,
  getNutritionStats,
  getAssignMealData,
} from "../../../services/clientDetails";

// Type definitions based on the API response

export interface VitalLog {
  name: string;
  unit: string;
  vital_log_value: string | null; // NEW: can be null
  date: string;
}

export interface WaterLog {
  intake_time: string;
  intake_ml: number;
}

// =============================================================================
// OLD RESPONSE — comment this block + uncomment NEW block to switch
// water_cycle shape: { date, target_water: { unit, value, value_ml }, total_intake_ml, water_logs[] }
// =============================================================================
export interface WaterCycle {
  date: string;
  target_water: {
    unit: string;
    value: number;
    value_to_ml: number;
  };
  total_intake_ml: number;
  water_logs: WaterLog[];
}
// =============================================================================

// =============================================================================
// NEW RESPONSE — comment this block + uncomment OLD block to revert
// water_cycle shape: flat object — no target_water object, no water_logs[]
//   value       → target in litres (string)
//   total_intake → intake in ml (number)
// =============================================================================
// export interface WaterCycle {
//   date: string;
//   unit: string;
//   value: string;
//   total_intake: number;
// }
// =============================================================================

export interface SleepCycle {
  cycleno: number;
  mood: string;
  slept_time_hour: number;
  slept_time_minute: number;
  slept_time_period: string;
  awake_time_hour: number;
  awake_time_minute: number;
  awake_time_period: string;
  type: string;
  date?: string; // NEW: added
}

export interface PoopCycle {
  cycleno: number;
  mood: string;
  cycle_hours: number;
  cycle_minutes: number;
  comment: string;
  ampm: string;
  cycle_id: string;
  date: string;
}

// =============================================================================
// OLD RESPONSE — comment this block + uncomment NEW block to switch
// exercises shape: { duration_minutes, comment, levels, level_value, total_calories }
// =============================================================================
export interface Exercise {
  date: string;
  exercise_name: string;
  starting_time: string;
  duration_minutes: number;
  comment: string;
  levels: string;
  level_value: string;
  total_calories: number;
}
// =============================================================================

// =============================================================================
// NEW RESPONSE — comment this block + uncomment OLD block to revert
// exercises shape: duration_minutes → duration_in_minutes, added entry_id + exercise_id
// =============================================================================
// export interface Exercise {
//   date: string;
//   entry_id: number;          // NEW
//   exercise_id: number;       // NEW
//   exercise_name: string;
//   starting_time: string;
//   duration_in_minutes: number; // NEW: was duration_minutes
//   comment: string;
//   levels: string;
//   level_value: string;
//   total_calories: number;
// }
// =============================================================================

// =============================================================================
// OLD RESPONSE — comment this block + uncomment NEW block to switch
// supplement_details shape: { supplement_name, when_to_take, strength_value,
//                             frequency, dosage, dosage_unit }
// =============================================================================
export interface Supplement {
  date: string;
  medicine_name: string;    // was supplement_name
  salt_name: string;
  quantity: string;         // replaces dosage + dosage_unit
  consume_with: string;
  meal_type: string;
  before_after_meal: string; // was when_to_take
  status: string;
  hour: number;
  minute: number;
  period: string;
  actual_date?: string;
}
// =============================================================================

// =============================================================================
// NEW RESPONSE — comment this block + uncomment OLD block to revert
// supplement_details shape: supplement_name → medicine_name,
//                            when_to_take → before_after_meal,
//                            quantity added, strength_value/frequency/dosage/dosage_unit removed
// =============================================================================
// export interface Supplement {
//   date: string;
//   medicine_name: string;    // NEW: was supplement_name
//   salt_name: string;
//   quantity: string;         // NEW: replaces dosage + dosage_unit
//   consume_with: string;
//   meal_type: string;
//   before_after_meal: string; // NEW: was when_to_take
//   status: string;
//   hour: number;
//   minute: number;
//   period: string;
//   actual_date?: string;     // NEW
// }
// =============================================================================

// =============================================================================
// OLD RESPONSE — comment this block + uncomment NEW block to switch
// medicine_details shape: { strength, frequency, dosage, dosage_unit, when_to_take }
// =============================================================================
export interface Medicine {
  date: string;
  medicine_name: string;
  salt_name: string | null;
  strength: string;
  frequency: string;
  consume_with: string;
  meal_type: string;
  when_to_take: string;
  dosage: string;
  dosage_unit: string | null;
  status: string;
  hour: number;
  minute: number;
  period: string;
}
// =============================================================================

// =============================================================================
// NEW RESPONSE — comment this block + uncomment OLD block to revert
// medicine_details shape: strength/frequency/dosage/dosage_unit removed,
//                          quantity + before_after_meal + medicine_type added
// =============================================================================
// export interface Medicine {
//   date: string;
//   medicine_type: string;     // NEW
//   medicine_name: string;
//   salt_name: string | null;
//   quantity: string;          // NEW: replaces dosage + dosage_unit
//   consume_with: string;
//   meal_type: string;
//   before_after_meal: string; // NEW: was when_to_take
//   status: string;
//   hour: number;
//   minute: number;
//   period: string;
//   actual_date?: string;      // NEW
// }
// =============================================================================

export interface NutritionData {
  total_cals: number;
  cals_units: string;
  fat: number;
  fat_units: string;
  carbs: number;
  carbs_units: string;
  protein: number;
  protein_units: string;
  GL: number;
  GL_units: string;
  CPF: string;
}

export interface LoggedNutritionData {
  total_calories_logged: string;
  total_fat_logged: string;
  total_carbs_logged: string;
  total_GL_logged: string;
  total_AAFAdj_protein_logged: string;
  total_CPF: string;
}

export interface FoodData {
  food_id: number;
  option_id: number;
  item_name: string;
  picture: string;
  units: string;
  measure_unit_type: string;
  measure_unit_id: number;
  base_unit_quantity?: number;
  base_unit_type?: string;
  oil: string;
  GL: string;
  cals: string;
  AAFAdj_protein: string;
  total_EAA: string;
  lysine: string;
  carbs: string;
  hidden_fat: string;
  potassium: string;
  gross_protein: string;
  tdf: string;
  AA_factor: string;
  quantity: string;
  meal_food_id?: number;
  fk_user_option?: number;
}

export interface MealOption {
  log_id: number | null;
  meal_plan: string;
  option_no: number;
  option_id: number;
  option_name: string;
  option_instructions: string;
  meal_type_id: number;
  meal_type: string;
  status: string;
  total_cals: number;
  total_AAFAdj_protein: number;
  total_carbs: number;
  total_fat: number;
  total_GL: number;
  option_total_cpf: string;
  meal_planning_status?: boolean;
  food_data: FoodData[];
  hour?: number;
  minute?: number;
  period?: string;
  date?: string;
  images?: string[];
}

export interface LogOption {
  log_id: number;
  hour?: number;
  minute?: number;
  period?: string;
  logged_time?: string;
  date: string;
  meal_plan: string;
  meal_plan_status: string;
  log_type: string;
  images: string[];
  options: MealOption[];
}

export interface MealData {
  user_meal_type: string;
  user_meal_type_id: number;
  fk_user_meal: number;
  meal_type_hour: number;      // ✅ add this
  meal_type_min: number;       // ✅ add this
  meal_type_period: string;    // ✅ add this
  meal_time?: string;          // keep as optional for backward compat
  meal_type_date: string;
  is_custom_meal_type: boolean;
  log_options: LogOption[];
  Options: MealOption[];
  Custom_Options: MealOption[];
}

export interface AllLogsData {
  vital_log: VitalLog[];
  water_cycle: WaterCycle[];
  sleep_cycle_details: SleepCycle[];
  poop_cycle_details: PoopCycle[];
  exercises: Exercise[];
  supplement_details: Supplement[];
  medicine_details: Medicine[];
  nutrition_stats?: {
    nutritionist_nutrients: NutritionData;
    logged_data: LoggedNutritionData;
  };
  meal_data?: MealData[];
  meal_plan_info?: {
    user_id: number;
    nutritionist_id: number;
    meal_plan_id: number;
    meal_plan_name: string;
    meal_plan_type: string;
    meal_plan_type_for_current_date: string;
    activation_date: string;
    past_meal_plan_type_activation_date: string;
    meal_plan_instructions: string;
  };
}

interface AllLogsResponse {
  success: string;
  message: string;
  data: AllLogsData;
}

interface NutritionStatsResponse {
  success: string;
  message: string;
  nutritionist_nutrients: NutritionData;
  total_calories_logged: string;
  total_fat_logged: string;
  total_carbs_logged: string;
  total_GL_logged: string;
  total_AAFAdj_protein_logged: string;
  total_CPF: string;
}

interface MealDataResponse {
  success: string;
  message: string;
  user_id: number;
  nutritionist_id: number;
  meal_plan_id: number;
  meal_plan_name: string;
  meal_plan_type: string;
  meal_plan_type_for_current_date: string;
  activation_date: string;
  past_meal_plan_type_activation_date: string;
  meal_plan_instructions: string;
  meal_data: MealData[];
  nutritionist_nutrients: NutritionData;
  total_calories_logged: string;
  total_fat_logged: string;
  total_carbs_logged: string;
  total_GL_logged: string;
  total_AAFAdj_protein_logged: string;
  cpf_logged_string: string;
}

interface UseAllLogsDataReturn {
  data: AllLogsData | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// Helper function to format date in local timezone
const formatDateToLocalString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper function to format date for nutrition API (no leading zero for month)
const formatDateForNutritionAPI = (dateStr: string): string => {
  const [year, month, day] = dateStr.split("-");
  return `${year}-${parseInt(month)}-${day}`;
};

const allLogsFetcher = async (
  [, userId, formattedDate]: readonly ["allLogs", number, string]
): Promise<AllLogsData> => {
  const nutritionPayload = { user_id: userId, date: formatDateForNutritionAPI(formattedDate) };
  const mealPayload = { user_id: userId, date: formattedDate };

  const [logsResult, nutritionResult, mealResult] = await Promise.allSettled([
    getAllLogsDetails(userId, formattedDate),
    getNutritionStats(nutritionPayload),
    getAssignMealData(mealPayload),
  ]);

  if (logsResult.status === "rejected") {
    throw new Error(`Failed to fetch logs: ${logsResult.reason?.message || "Network error"}`);
  }
  const logsData: AllLogsResponse = logsResult.value;
  if (logsData.success !== "1") {
    throw new Error(logsData.message || "Failed to fetch logs data");
  }

  let nutritionStats: AllLogsData["nutrition_stats"];
  if (nutritionResult.status === "fulfilled") {
    try {
      const nutritionData: NutritionStatsResponse = nutritionResult.value;
      if (nutritionData.success === "1") {
        nutritionStats = {
          nutritionist_nutrients: nutritionData.nutritionist_nutrients,
          logged_data: {
            total_calories_logged: nutritionData.total_calories_logged,
            total_fat_logged: nutritionData.total_fat_logged,
            total_carbs_logged: nutritionData.total_carbs_logged,
            total_GL_logged: nutritionData.total_GL_logged,
            total_AAFAdj_protein_logged: nutritionData.total_AAFAdj_protein_logged,
            total_CPF: nutritionData.total_CPF,
          },
        };
      }
    } catch (err) {
      console.warn("useAllLogsData: Failed to parse nutrition stats:", err);
    }
  }

  let mealData: MealData[] | undefined;
  let mealPlanInfo: AllLogsData["meal_plan_info"];
  if (mealResult.status === "fulfilled") {
    try {
      const mealResponseData: MealDataResponse = mealResult.value;
      if (mealResponseData.success === "1") {
        mealData = mealResponseData.meal_data;
        mealPlanInfo = {
          user_id: mealResponseData.user_id,
          nutritionist_id: mealResponseData.nutritionist_id,
          meal_plan_id: mealResponseData.meal_plan_id,
          meal_plan_name: mealResponseData.meal_plan_name,
          meal_plan_type: mealResponseData.meal_plan_type,
          meal_plan_type_for_current_date: mealResponseData.meal_plan_type_for_current_date,
          activation_date: mealResponseData.activation_date,
          past_meal_plan_type_activation_date: mealResponseData.past_meal_plan_type_activation_date,
          meal_plan_instructions: mealResponseData.meal_plan_instructions,
        };
      }
    } catch (err) {
      console.warn("useAllLogsData: Failed to parse meal data:", err);
    }
  }

  return {
    vital_log: logsData.data.vital_log || [],
    water_cycle: logsData.data.water_cycle || [],
    sleep_cycle_details: logsData.data.sleep_cycle_details || [],
    poop_cycle_details: logsData.data.poop_cycle_details || [],
    exercises: logsData.data.exercises || [],
    supplement_details: logsData.data.supplement_details || [],
    medicine_details: logsData.data.medicine_details || [],
    nutrition_stats: nutritionStats,
    meal_data: mealData,
    meal_plan_info: mealPlanInfo,
  };
};

export const useAllLogsData = (
  userId: number,
  selectedDate?: string
): UseAllLogsDataReturn => {
  const isValidUser = !!userId && !isNaN(userId) && userId > 0;
  const formattedDate = selectedDate || formatDateToLocalString(new Date());
  const key = isValidUser ? (["allLogs", userId, formattedDate] as const) : null;

  const { data, error, isLoading, mutate } = useSWR<AllLogsData>(key, allLogsFetcher, {
    // Poll only while the tab is visible so a backgrounded dashboard doesn't burn API calls.
    refreshInterval: () => (document.visibilityState === "visible" ? 15000 : 0),
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    keepPreviousData: true,
    dedupingInterval: 2000,
    focusThrottleInterval: 5000,
  });

  const errorMessage = !isValidUser
    ? "Invalid user ID"
    : error
      ? error instanceof Error
        ? error.message
        : "Failed to load logs data"
      : null;

  return {
    data: data ?? null,
    loading: isValidUser && isLoading,
    error: errorMessage,
    refetch: () => {
      void mutate();
    },
  };
};